<template>
    <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <!-- Sidebar -->
      <AdminSidebar :isOpen="isSidebarOpen" />
  
      <!-- Main Content -->
      <main 
        class="flex-1 overflow-x-hidden transition-all duration-300"
        :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
      >
        <!-- Navbar -->
       <header 
        class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm transition-all duration-300"
      >
        <AdminTopbar 
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>
  
        <!-- Content Area -->
        <div class="p-6 mt-2 max-w-full">
          <div class="flex justify-between items-center mb-8">
            <div class="flex items-center space-x-4">
              <span class="text-gray-600 dark:text-gray-300">Last updated: {{ currentDate }}</span>
              <button 
                @click="refreshData"
                class="p-2 rounded-full bg-white dark:bg-gray-700 shadow hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
  
          <!-- Summary Cards -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <!-- Total Staff -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border-l-4 border-[#035a76] dark:border-[#035a76]">
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-sm font-medium text-[#035a76] dark:text-[#035a76]">Total Staff</p>
                  <p class="text-2xl font-bold text-[#035a76] dark:text-[#035a76] mt-1">{{ totalStaff }}</p>
                </div>
                <div class="p-3 rounded-full bg-blue-100 dark:bg-[#035a76] text-[#035a76] dark:text-[#035a76]">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-[#035a76] dark:text-[#035a76] mt-2">All teaching and non-teaching staff</p>
            </div>
  
            <!-- Staff Per Section -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border-l-4 border-green-500 dark:border-green-400">
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-sm font-medium text-[#035a76] dark:text-[#035a76]">Teaching Staff</p>
                  <p class="text-2xl font-bold text-[#035a76] dark:text-[#035a76] mt-1">{{ teachingStaff }}</p>
                  <div class="flex space-x-2 mt-1">
                    <span class="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded">N: {{ staffBySection.nursery }}</span>
                    <span class="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">P: {{ staffBySection.primary }}</span>
                    <span class="text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded">S: {{ staffBySection.secondary }}</span>
                  </div>
                </div>
                <div class="p-3 rounded-full bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-[#035a76] dark:text-[#035a76] mt-2">N=Nursery, P=Primary, S=Secondary</p>
            </div>
  
            <!-- Outsourced Staff -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border-l-4 border-yellow-500 dark:border-yellow-400">
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-sm font-medium text-[#035a76] dark:text-[#035a76]">Outsourced Staff</p>
                  <p class="text-2xl font-bold text-[#035a76] dark:text-[#035a76] mt-1">{{ outsourcedStaff }}</p>
                  <div class="flex space-x-2 mt-1">
                    <span class="text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded">Security: {{ outsourcedByType.security }}</span>
                    <span class="text-xs bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-2 py-1 rounded">Cleaning: {{ outsourcedByType.cleaning }}</span>
                  </div>
                </div>
                <div class="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-[#035a76] dark:text-[#035a76] mt-2">Contracted external personnel</p>
            </div>
  
            <!-- Extracurricular Teachers -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border-l-4 border-purple-500 dark:border-purple-400">
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-sm font-medium text-[#035a76] dark:text-[#035a76]">Extracurricular Staff</p>
                  <p class="text-2xl font-bold text-[#035a76] dark:text-[#035a76] mt-1">{{ extracurricularStaff }}</p>
                  <div class="flex space-x-2 mt-1">
                    <span class="text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded">Sports: {{ extracurricularByType.sports }}</span>
                    <span class="text-xs bg-pink-100 dark:bg-pink-900 text-pink-800 dark:text-pink-200 px-2 py-1 rounded">Arts: {{ extracurricularByType.arts }}</span>
                  </div>
                </div>
                <div class="p-3 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
              </div>
              <p class="text-xs text-[#035a76] dark:text-[#035a76] mt-2">Part-time specialized instructors</p>
            </div>
          </div>
  
          <!-- Charts Section -->
          <div class="space-y-6 mb-8">
            <!-- Staff Distribution Chart -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md overflow-x-auto">
              <div class="min-w-[600px]">
                <div class="flex justify-between items-center mb-4">
                  <h2 class="text-lg font-semibold text-[#035a76] dark:text-[#035a76]">Staff Distribution</h2>
                  <select 
                    v-model="selectedPeriod"
                    class="text-sm border rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                    <option value="all">All Time</option>
                  </select>
                </div>
                <div id="staffChart" class="min-h-[350px]"></div>
              </div>
            </div>
  
            <!-- Staff Composition Chart -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md overflow-x-auto">
              <div class="min-w-[600px]">
                <div class="flex justify-between items-center mb-4">
                  <h2 class="text-lg font-semibold text-[#035a76] dark:text-[#035a76]">Staff Composition</h2>
                  <select 
                    v-model="selectedPeriod"
                    class="text-sm border rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                    <option value="all">All Time</option>
                  </select>
                </div>
                <div id="compositionChart" class="min-h-[350px]"></div>
              </div>
            </div>
          </div>
  
          <!-- Recent Activity Section -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-lg font-semibold text-[#035a76] dark:text-[#035a76]">Recent HR Activity</h2>
              <button class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">View All</button>
            </div>
            <div class="space-y-4">
              <div v-for="(activity, index) in recentActivities" :key="index" class="flex items-start">
                <div :class="`p-2 rounded-full ${activity.color} mr-3`">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path :stroke-linecap="activity.icon.cap" :stroke-linejoin="activity.icon.join" :stroke-width="2" :d="activity.icon.path" />
                  </svg>
                </div>
                <div>
                  <p class="font-medium dark:text-gray-200">{{ activity.title }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ activity.description }}</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">{{ activity.time }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, watch, computed } from 'vue';
  import ApexCharts from 'apexcharts';
  import AdminSidebar from '../../components/admin/AdminSidebar.vue';
  import AdminTopbar from '../../components/admin/AdminTopbar.vue';
  
  const isSidebarOpen = ref(true);
  const isDarkMode = ref(false);
  const selectedPeriod = ref('month');
  
  // Staff data
  const totalStaff = ref(125);
  const teachingStaff = ref(85);
  const nonTeachingStaff = ref(40);
  const outsourcedStaff = ref(15);
  const extracurricularStaff = ref(10);
  
  const staffBySection = ref({
    nursery: 20,
    primary: 35,
    secondary: 30
  });
  
  const outsourcedByType = ref({
    security: 8,
    cleaning: 5,
    maintenance: 2
  });
  
  const extracurricularByType = ref({
    sports: 6,
    arts: 4
  });
  
  // Computed property to ensure totals match
  const totalCalculated = computed(() => {
    return teachingStaff.value + nonTeachingStaff.value;
  });
  
  // Sample recent activities
  const recentActivities = ref([
    {
      title: 'New staff onboarded',
      description: 'John Doe joined as Primary Section Teacher',
      time: '2 hours ago',
      color: 'bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400',
      icon: {
        path: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
        cap: 'round',
        join: 'round'
      }
    },
    {
      title: 'Leave request submitted',
      description: 'Sarah Smith requested leave from 15-20 June',
      time: '1 day ago',
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400',
      icon: {
        path: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        cap: 'round',
        join: 'round'
      }
    },
    {
      title: 'Contract ended',
      description: 'Outsourced security guard Michael completed contract',
      time: '3 days ago',
      color: 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400',
      icon: {
        path: 'M6 18L18 6M6 6l12 12',
        cap: 'round',
        join: 'round'
      }
    }
  ]);
  
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  let staffChart = null;
  let compositionChart = null;
  
  const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value;
  };
  
  const toggleDarkMode = () => {
    isDarkMode.value = !isDarkMode.value;
    if (isDarkMode.value) {
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
    }
  };
  
  const refreshData = () => {
    // In a real app, this would fetch fresh data from the API
    console.log('Refreshing HR data...');
    renderCharts();
  };
  
  const renderCharts = () => {
    // Destroy existing charts if they exist
    if (staffChart) {
      staffChart.destroy();
    }
    if (compositionChart) {
      compositionChart.destroy();
    }
  
    // Chart colors based on dark mode
    const colors = isDarkMode.value ? {
      yellow: '#035a76',
      pink: '#63a43f',
      gree: '#fbbf24',
      blue: '#a78bfa',
      red: '#f87171',
      purple: '#f472b6'
    } : {
      yellow: '#035a76',
      pink: '#74bd54',
      green: '#f59e0b',
      blue: '#8b5cf6',
      red: '#ef4444',
      purple: '#ec4899'
    };
  
    const staffOptions = {
  series: [{
    name: 'Staff Count',
    data: [
      staffBySection.value.nursery,
      staffBySection.value.primary,
      staffBySection.value.secondary,
      nonTeachingStaff.value,
      outsourcedStaff.value,
      extracurricularStaff.value
    ]
  }],
  chart: {
    type: 'bar',
    height: 350,
    toolbar: {
      show: false
    },
    background: 'transparent',
    foreColor: isDarkMode.value ? '#fff' : '#374151'
  },
  colors: [
    '#fae240',
    '#e8037e', 
    '#76ba51', 
    '#035a76',  
    '#ef4444', 
    '#6366f1'  
  ],
  plotOptions: {
    bar: {
      borderRadius: 4,
      horizontal: false,
      distributed: true 
    }
  },
  dataLabels: {
    enabled: false
  },
  xaxis: {
    categories: ['Nursery', 'Primary', 'Secondary', 'Non-Teaching', 'Outsourced', 'Extracurricular'],
    labels: {
      style: {
        colors: isDarkMode.value ? '#9ca3af' : '#6b7280'
      }
    }
  },
  yaxis: {
    title: {
      text: 'Number of Staff',
      style: {
        color: isDarkMode.value ? '#9ca3af' : '#6b7280'
      }
    },
    labels: {
      style: {
        colors: isDarkMode.value ? '#9ca3af' : '#6b7280'
      }
    }
  },
  tooltip: {
    y: {
      formatter: function(val) {
        return val + " staff";
      }
    }
  },
  grid: {
    borderColor: isDarkMode.value ? '#4b5563' : '#e5e7eb'
  }
};

  
    // Staff Composition Chart (Pie)
    const compositionOptions = {
      series: [teachingStaff.value, nonTeachingStaff.value, outsourcedStaff.value, extracurricularStaff.value],
      chart: {
        type: 'donut',
        height: 350,
        background: 'transparent',
        foreColor: isDarkMode.value ? '#fff' : '#374151'
      },
      colors: [colors.blue, colors.green, colors.yellow, colors.purple],
      labels: ['Teaching Staff', 'Non-Teaching', 'Outsourced', 'Extracurricular'],
      legend: {
        position: 'bottom',
        labels: {
          colors: isDarkMode.value ? '#fff' : '#000'
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return Math.round(val) + "%"
        },
        style: {
          colors: isDarkMode.value ? ['#fff'] : ['#000']
        }
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total Staff',
                color: isDarkMode.value ? '#fff' : '#000',
                formatter: function (w) {
                  return w.globals.seriesTotals.reduce((a, b) => {
                    return a + b
                  }, 0)
                }
              }
            }
          }
        }
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 200
          },
          legend: {
            position: 'bottom'
          }
        }
      }]
    };
  
    staffChart = new ApexCharts(document.querySelector("#staffChart"), staffOptions);
    staffChart.render();
  
    compositionChart = new ApexCharts(document.querySelector("#compositionChart"), compositionOptions);
    compositionChart.render();
  };
  
  onMounted(() => {
    renderCharts();
    
    // Check for saved dark mode preference
    if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      isDarkMode.value = true;
      document.documentElement.classList.add('dark');
    } else {
      isDarkMode.value = false;
      document.documentElement.classList.remove('dark');
    }
  });
  
  // Watch for changes and update charts
  watch([selectedPeriod, isDarkMode], () => {
    renderCharts();
  }, { deep: true });
  
  // Watch to ensure totals match
  watch([teachingStaff, nonTeachingStaff], () => {
    totalStaff.value = teachingStaff.value + nonTeachingStaff.value;
  });
  </script>
  
  <style scoped>
  .router-link-active {
    background-color: #04698f;
    color: white !important;
    font-weight: 600;
  }
  
  body {
    font-family: 'Inter', sans-serif;
  }
  
  /* Smooth transitions for dark mode */
  .bg-white {
    transition: background-color 0.3s ease;
  }
  .text-gray-800 {
    transition: color 0.3s ease;
  }
  .border-gray-200 {
    transition: border-color 0.3s ease;
  }
  </style>